import React, { useState, useEffect, useLayoutEffect } from 'react';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';
import { removeSupportChat, collapseIframe, expandIframe } from '../../../helpers/helpers';
import {
  Card,
  CloseButton,
  IconArrowOpenUpLine,
  IconButton,
  IconArrowOpenDownLine,
} from '@smarterservices/smarter-ui';
import { useAppStore } from '../../../stores/app';

const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useLayoutEffect(() => {
    // Function to update the height state
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    updateHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateHeight);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return windowHeight;
};

const SupportChat = () => {
  const [token, setToken] = useState(null);
  const [sessionSid, setSessionSid] = useState(null);
  const [examSid, setExamSid] = useState(null);
  const [installSid, setInstallSid] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const windowHeight = useWindowHeight();
  const { getSessionStartData } = useAppStore();
  // watch for the initialized state in the app store
  const appInitialized = useAppStore(
    (state) => state.initialized
  );
  const initialQueryParams = useAppStore(
    (state) => state.initialQueryParams
  );

  useEffect(() => {
    // Handle messages from the iframe
    const handleMessage = (event) => {
      if (event.data === 'closeSupportChat') {
        removeSupportChat();
      }
    };

    window.addEventListener('message', handleMessage);

    const initChat = async () => {
      if(appInitialized){
        const storeState = useAppStore.getState();

        // Validate required session data exists
        if (!storeState.token || !storeState.sessionSid || !storeState.examSid || !storeState.installSid) {
          console.error('Missing required session data in store');
          return;
        }

        setToken(storeState.token);
        setSessionSid(storeState.sessionSid);
        setExamSid(storeState.examSid);
        setInstallSid(storeState.installSid);

        // TODO: here we can call the API to get any additional info needed for enrichment (stepData, etc)
        //const sessionStartData = await getSessionStart(chatParams);

        // Set expanded state and collapse iframe if needed
        const isExpanded = initialQueryParams?.isExpanded === 'true' ?? true;
        setIsExpanded(isExpanded);

        if (!isExpanded) {
          collapseIframe('spSupportChatIframeId');
        }
      }
    };
    initChat();

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [appInitialized]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe('spSupportChatIframeId');
    } else {
      expandIframe('spSupportChatIframeId');
    }
  };

  window.addEventListener('message', (event) => {
    if (event.data.type === 'sp_iframe_expand_state_change') {
      const isExpanded = event.data.isExpanded;
      setIsExpanded(isExpanded);
    }
  });

  const HEADER_HEIGHT = 50;
  const bodyHeight =
    windowHeight > 0 ? `${windowHeight - HEADER_HEIGHT}px` : '100vh';

  return sessionSid && examSid && installSid && token ? (
    <Card
      background="secondary"
      borderColor="brand"
      borderWidth="large"
      borderRadius="x-small"
    >
      <Card.Header background="brand">
        <Card.Title>Support Chat</Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={removeSupportChat}
          />
        </Card.Actions>
      </Card.Header>
      {isExpanded && (
        <Card.Body padding="0" height={bodyHeight}>
          <iframe
            id="spSupportChatIframeId"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'transparent'
            }}
            sandbox="allow-scripts allow-same-origin"
            title="Support Chat"
            srcDoc={`
              <!DOCTYPE html>
              <html>
                <head>
                  <style>
                    body { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                    #intercom-container { height: 100vh; width: 100%; }
                    #chat-button { display: none; }
                  </style>
                  <script>
                    // Initialize Intercom with the provided parameters
                    window.inAppHelpOptions = ${JSON.stringify({
                      token,
                      application_sid: 'AP6800b9e2e1794dbbbf6ad866194821d3',
                      context: 'in_session',
                      sids: {
                        session_sid: sessionSid,
                        exam_sid: examSid,
                        install_sid: installSid
                      }
                    }).replace(/</g, '\\u003c')};
                    
                    // Load the Intercom script
                    const script = document.createElement('script');
                    script.src = 'https://help.smarterservices.com/embed/in-app-widget.js';
                    script.onload = function() {
                      if (window.initHelpCenter) window.initHelpCenter();
                      
                      // Poll for Intercom to be fully ready
                      let attempts = 0;
                      const maxAttempts = 50;
                      const checkInterval = 100;
                      
                      const waitForIntercom = setInterval(() => {
                        attempts++;
                        
                        if (window.Intercom && typeof window.Intercom === 'function') {
                          clearInterval(waitForIntercom);
                          try {
                            window.Intercom('show');
                            window.Intercom('onHide', function() {
                              console.log('Intercom hidden');
                              // Send message to parent window to close the chat
                              window.parent.postMessage('closeSupportChat', '*');
                            });
                          } catch (e) {
                            console.error('Error showing Intercom:', e);
                          }
                        } else if (attempts >= maxAttempts) {
                          clearInterval(waitForIntercom);
                          console.error('Intercom failed to initialize after', maxAttempts * checkInterval, 'ms');
                        }
                      }, checkInterval);
                    };
                    document.head.appendChild(script);
                  </script>
                </head>
                <body>
                  <div id="intercom-container"></div>
                </body>
              </html>
            `}
          />
        </Card.Body>
      )}
    </Card>
  ) : null;
};

export default SupportChat;